import React from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Spin, Alert } from 'antd';

// Configure PDF.js worker for v2.16.105
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

type Props = {
  url: string,
  isMobile?: boolean,
  onLoadSuccess?: (numPages: number) => void,
  onLoadError?: (error: any) => void,
  onScroll?: (e: any) => void,
}

export default class PdfViewer extends React.PureComponent<Props> {
  state = {
    numPages: null,
    pageWidth: null,
    loading: true,
    error: null,
  };

  containerRef = React.createRef();

  componentDidMount() {
    this.updatePageWidth();
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.updatePageWidth();
    }, 250);
  };

  updatePageWidth = () => {
    if (this.containerRef.current) {
      const containerWidth = this.containerRef.current.offsetWidth;
      // Subtract padding/margins
      const pageWidth = Math.min(containerWidth - 32, 800);
      this.setState({ pageWidth });
    }
  };

  onDocumentLoadSuccess = ({ numPages }) => {
    this.setState({ numPages, loading: false });
    if (this.props.onLoadSuccess) {
      this.props.onLoadSuccess(numPages);
    }
  };

  onDocumentLoadError = (error) => {
    this.setState({
      loading: false,
      error: `Failed to load PDF document: ${error.message || 'Unknown error'}. Please try refreshing the page.`
    });

    if (this.props.onLoadError) {
      this.props.onLoadError(error);
    }
  };

  render() {
    const { url, isMobile, onScroll } = this.props;
    const { numPages, pageWidth, loading, error } = this.state;

    if (error) {
      return (
        <Alert
          message="PDF Load Error"
          description={error}
          type="error"
          showIcon
          style={{ margin: 16 }}
        />
      );
    }

    return (
      <div
        ref={this.containerRef}
        onScroll={onScroll}
        style={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: isMobile ? 'transparent' : '#f5f5f5',
        }}
      >
        {loading && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '60px 0',
          }}>
            <Spin size="large" tip="Loading PDF..." />
          </div>
        )}

        <Document
          file={url}
          onLoadSuccess={this.onDocumentLoadSuccess}
          onLoadError={this.onDocumentLoadError}
          loading={null}
          error={null}
          options={{
            cMapUrl: `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/cmaps/`,
            cMapPacked: true,
          }}
        >
          {pageWidth && numPages && Array.from(
            new Array(numPages),
            (el, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                width={pageWidth}
                loading={null}
                error={null}
                style={{
                  marginBottom: isMobile ? 8 : 16,
                  boxShadow: isMobile ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
                }}
              />
            )
          )}
        </Document>

        {!loading && numPages && (
          <div style={{
            textAlign: 'center',
            padding: '16px',
            color: '#666',
            fontSize: '12px',
          }}>
            {numPages} {numPages === 1 ? 'page' : 'pages'}
          </div>
        )}

      </div>
    );
  }
}
